import { useEffect, useState, useRef } from "react";
import { Stack, TextField, ITextFieldStyles } from "@fluentui/react";
import { Button, Tooltip } from "@fluentui/react-components";
import { Send28Filled } from "@fluentui/react-icons";

import styles from "./QuestionInput.module.css";

// Gemini-style TextField styles
const textFieldStyles: Partial<ITextFieldStyles> = {
    fieldGroup: {
        background: "#1e1e1e",
        border: "1px solid #303030",
        borderRadius: "24px",
        selectors: {
            ":hover": {
                background: "#252525",
                border: "1px solid #3a3a3a",
            },
            ":focus-within": {
                background: "#252525",
                border: "1px solid #3a3a3a",
            },
        },
    },
    field: {
        background: "transparent",
        color: "#ffffff",
        border: "none",
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "22px",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        padding: "8px 60px 8px 12px",
        height: "100px",
        minHeight: "100px",
        borderRadius: "24px",
        selectors: {
            "::placeholder": {
                color: "rgba(255, 255, 255, 0.5)",
            },
        },
    },
    wrapper: {
        background: "transparent",
    },
};

interface Props {
    onSend: (question: string) => void;
    disabled: boolean;
    initQuestion?: string;
    placeholder?: string;
    clearOnSend?: boolean;
}

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, initQuestion }: Props) => {
    const [question, setQuestion] = useState<string>("");

    useEffect(() => {
        initQuestion && setQuestion(initQuestion);
    }, [initQuestion]);

    // iOS keyboard handling - adjust viewport when keyboard appears
    useEffect(() => {
        // Only run on mobile devices
        const isMobile = window.matchMedia('(max-width: 768px)').matches;
        if (!isMobile || !window.visualViewport) {
            return;
        }

        const handleViewportResize = () => {
            if (window.visualViewport) {
                // Calculate keyboard height
                const viewportHeight = window.visualViewport.height;
                const windowHeight = window.innerHeight;
                const keyboardHeight = windowHeight - viewportHeight;

                // Update CSS custom property for keyboard offset
                document.documentElement.style.setProperty(
                    '--keyboard-offset',
                    `${keyboardHeight}px`
                );
            }
        };

        // Listen for viewport resize (keyboard open/close)
        window.visualViewport.addEventListener('resize', handleViewportResize);
        window.visualViewport.addEventListener('scroll', handleViewportResize);

        // Cleanup
        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleViewportResize);
                window.visualViewport.removeEventListener('scroll', handleViewportResize);
            }
            document.documentElement.style.setProperty('--keyboard-offset', '0px');
        };
    }, []);

    const sendQuestion = () => {
        if (disabled || !question.trim()) {
            return;
        }

        onSend(question);

        if (clearOnSend) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        if (!newValue) {
            setQuestion("");
        } else if (newValue.length <= 500) {
            setQuestion(newValue);
        }
    };

    // Apply Gemini-style directly via DOM to override all CSS
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (wrapperRef.current) {
            const fieldGroup = wrapperRef.current.querySelector('.ms-TextField-fieldGroup') as HTMLElement;
            if (fieldGroup) {
                fieldGroup.style.setProperty('background', '#1e1e1e', 'important');
                fieldGroup.style.setProperty('border', '1px solid #303030', 'important');
                fieldGroup.style.setProperty('border-radius', '24px', 'important');
            }
        }
    });

    return (
        <Stack horizontal className={styles.questionInputContainer}>
            <div className={styles.questionInputWrapper} ref={wrapperRef}>
                <TextField
                    className={styles.questionInputTextArea}
                    placeholder={placeholder}
                    multiline
                    resizable={false}
                    borderless
                    value={question}
                    onChange={onQuestionChange}
                    onKeyDown={onEnterPress}
                    styles={textFieldStyles}
                />
                <div className={`${styles.charCounter} ${question.length > 450 ? styles.charCounterWarning : ''} ${question.length >= 500 ? styles.charCounterLimit : ''}`}>
                    <span className={styles.charCounterText}>{question.length}</span>
                    <span className={styles.charCounterDivider}>/</span>
                    <span className={styles.charCounterMax}>500</span>
                </div>
            </div>
            <div className={styles.questionInputButtonsContainer}>
                
                {/*<Tooltip content="Ask question button" relationship="label">*/}
                    <button 
                        className={styles.sendButton}
                        onClick={sendQuestion}
                        disabled={disabled || !question.trim()}
                        aria-label="Send message"
                    >
                        <Send28Filled className={styles.sendIcon} />
                    </button>
                {/*</Tooltip >*/}
                
            </div>
        </Stack>
    );
};
